import { useState, useEffect } from 'react';
import { Card, Badge, Button } from '@atoms/index';
import { ReviewList, ReviewResponseModal, AlertModal } from '@organisms/index';
import { useRestaurantStore } from '@store/restaurantStore';
import { reviewService } from '@services/reviewService';
import { storage, STORAGE_KEYS } from '@/utils/storage';
import { formatPrice } from '@/utils/format';
import type { Review, AuthTokens } from '@/types';

const AnalyticsTab = () => {
  const {
    restaurant,
    orders,
    loadStats,
    loadTopProducts,
    loadRevenueChartData,
    stats,
    topProducts,
    revenueChartData,
  } = useRestaurantStore();
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('today');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; variant: 'warning' | 'danger' }>({ isOpen: false, title: '', message: '', variant: 'warning' });

  useEffect(() => {
    loadStats(selectedPeriod);
    loadTopProducts(selectedPeriod);
    loadRevenueChartData(selectedPeriod);
    loadReviews();
  }, [selectedPeriod, loadStats, loadTopProducts, loadRevenueChartData]);

  useEffect(() => {
    if (revenueChartData.length > 0) {
      console.log('Revenue Chart Data:', revenueChartData);
      console.log('Total Revenue:', revenueChartData.reduce((sum, d) => sum + (Number(d.revenue) || 0), 0));
    }
  }, [revenueChartData]);

  const loadReviews = async () => {
    setIsLoadingReviews(true);
    try {
      const result = await reviewService.getRestaurantReviews({ limit: 20 });
      setReviews(result.reviews);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const handleRespond = (reviewId: string) => {
    const review = reviews.find((r) => r.id === reviewId);
    setSelectedReview(review || null);
    setSelectedReviewId(reviewId);
  };

  const handleResponseSubmit = async (reviewId: string, response: string) => {
    try {
      await reviewService.addRestaurantResponse(reviewId, response);
      await loadReviews();
      setSelectedReviewId(null);
      setSelectedReview(null);
    } catch (error) {
      console.error('Failed to add response:', error);
      throw error;
    }
  };

  const handleExportSales = async () => {
    try {
      const baseURL = import.meta.env.VITE_API_URL || '/api';
      const url = `${baseURL}/restaurant/my/orders/reports/sales?format=csv`;
      
      const tokens = storage.get<AuthTokens>(STORAGE_KEYS.AUTH_TOKENS);
      const token = tokens?.access_token;
      
      if (!token) {
        setAlertState({ isOpen: true, title: 'Oturum gerekli', message: 'Oturum açmanız gerekiyor', variant: 'warning' });
        return;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Rapor indirilemedi');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `sales-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to export sales report:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Rapor indirilirken bir hata oluştu', variant: 'danger' });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex gap-3 justify-end">
          <Button
            variant={selectedPeriod === 'today' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedPeriod('today')}
          >
            Bugün
          </Button>
          <Button
            variant={selectedPeriod === 'week' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedPeriod('week')}
          >
            Bu Hafta
          </Button>
          <Button
            variant={selectedPeriod === 'month' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSelectedPeriod('month')}
          >
            Bu Ay
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-primary">
              {stats?.totalOrders ?? 0}
            </p>
            <p className="text-sm text-gray-600">Toplam Sipariş (Teslim Edilen)</p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-success">
              {stats ? formatPrice(stats.totalRevenue) : '₺0'}
            </p>
            <p className="text-sm text-gray-600">Toplam Gelir</p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-warning">
              {stats && stats.totalOrders > 0
                ? formatPrice(stats.totalRevenue / stats.totalOrders)
                : '₺0'}
            </p>
            <p className="text-sm text-gray-600">Ortalama Sipariş</p>
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Raporlar</h3>
            <p className="text-sm text-gray-600">Satış raporlarını indirin</p>
          </div>
          <Button variant="primary" onClick={handleExportSales}>
            📊 CSV İndir
          </Button>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-bold text-gray-900 mb-4">Gelir ve Sipariş Grafiği</h3>
        {revenueChartData.length === 0 ? (
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <p className="text-gray-500">Veri yok</p>
          </div>
        ) : (
          <RevenueChart data={revenueChartData} period={selectedPeriod} />
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-bold text-gray-900 mb-4">En Çok Satan Ürünler</h3>
        {topProducts.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>Henüz satış verisi yok</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topProducts.map((product, index) => (
              <div
                key={product.productId || product.productName}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 font-medium">#{index + 1}</span>
                  <div>
                    <span className="font-medium text-gray-900">{product.productName}</span>
                    <p className="text-sm text-gray-500">
                      {product.totalQuantity} adet • {product.orderCount} sipariş
                    </p>
                  </div>
                </div>
                <span className="font-bold text-primary">{formatPrice(product.totalRevenue)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Müşteri Yorumları</h3>
          <Badge variant="info">{reviews.length} Yorum</Badge>
        </div>
        <ReviewList
          reviews={reviews}
          isLoading={isLoadingReviews}
          onRespond={handleRespond}
        />
      </Card>

      {selectedReviewId && selectedReview && (
        <ReviewResponseModal
          isOpen={!!selectedReviewId}
          reviewId={selectedReviewId}
          customerComment={selectedReview.comment || undefined}
          rating={selectedReview.rating}
          onConfirm={handleResponseSubmit}
          onClose={() => {
            setSelectedReviewId(null);
            setSelectedReview(null);
          }}
        />
      )}

      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
};

function RevenueChart({
  data,
  period,
}: {
  data: Array<{ date: string; dateLabel?: string; revenue: number; orders: number }>;
  period: 'today' | 'week' | 'month';
}) {
  if (data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
        <p className="text-gray-500">Veri yok</p>
      </div>
    );
  }

  const numericData = data.map((d) => ({
    ...d,
    revenue: typeof d.revenue === 'string' ? parseFloat(d.revenue) : Number(d.revenue) || 0,
    orders: Number(d.orders) || 0,
  }));

  const maxRevenue = Math.max(...numericData.map((d) => d.revenue), 1);
  const maxOrders = Math.max(...numericData.map((d) => d.orders), 1);
  const chartHeight = 300;
  const chartWidth = Math.max(800, numericData.length * 30);
  const padding = { top: 30, right: 80, bottom: 80, left: 100 };

  const formatDateLabel = (item: { date: string; dateLabel?: string }) => {
    if (item.dateLabel) {
      if (period === 'today') {
        const timeMatch = item.dateLabel.match(/\d{2}:\d{2}/);
        return timeMatch ? timeMatch[0] : item.dateLabel;
      }
      const dateMatch = item.dateLabel.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        return `${dateMatch[3]}.${dateMatch[2]}`;
      }
      return item.dateLabel;
    }
    const date = new Date(item.date);
    if (period === 'today') {
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const getBarWidth = () => {
    const availableWidth = chartWidth - padding.left - padding.right;
    const barCount = numericData.length;
    const spacing = 2;
    return Math.max(4, (availableWidth / barCount) - spacing);
  };

  const barWidth = getBarWidth();

  return (
    <div className="overflow-x-auto bg-white rounded-lg p-6">
      <div className="flex items-center justify-end gap-4 mb-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-primary rounded"></div>
          <span className="text-gray-700 font-medium">Gelir (₺)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-success rounded"></div>
          <span className="text-gray-700 font-medium">Sipariş Sayısı</span>
        </div>
      </div>
      
      <svg 
        width={chartWidth} 
        height={chartHeight + padding.top + padding.bottom} 
        className="w-full min-w-full"
        viewBox={`0 0 ${chartWidth} ${chartHeight + padding.top + padding.bottom}`}
      >
        <defs>
          <linearGradient id="revenueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#E63946" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#E63946" stopOpacity="0.2" />
          </linearGradient>
        </defs>

        {/* Y-axis for Revenue (left) - with better spacing */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + chartHeight - ratio * chartHeight;
          const value = maxRevenue * ratio;
          return (
            <g key={`revenue-${ratio}`}>
              <line
                x1={padding.left}
                y1={y}
                x2={chartWidth - padding.right}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray="4 4"
              />
              <text
                x={padding.left - 20}
                y={y + 5}
                textAnchor="end"
                fontSize="12"
                fill="#374151"
                fontWeight="600"
              >
                {formatPrice(value)}
              </text>
            </g>
          );
        })}

        {/* Y-axis for Orders (right) - with better spacing */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + chartHeight - ratio * chartHeight;
          const value = Math.round(maxOrders * ratio);
          return (
            <g key={`orders-${ratio}`}>
              <text
                x={chartWidth - padding.right + 15}
                y={y + 5}
                textAnchor="start"
                fontSize="12"
                fill="#2A9D8F"
                fontWeight="600"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Bars for Revenue */}
        {numericData.map((d, index) => {
          const x = padding.left + (index * (barWidth + 2)) + (barWidth / 2);
          const revenueHeight = (d.revenue / maxRevenue) * chartHeight;
          const y = padding.top + chartHeight - revenueHeight;
          
          return (
            <g key={`revenue-bar-${index}`}>
              <rect
                x={x - barWidth / 2}
                y={y}
                width={barWidth}
                height={revenueHeight}
                fill="url(#revenueGradient)"
                rx="2"
                className="hover:opacity-80 transition-opacity cursor-pointer"
              />
              <title>
                {formatDateLabel(d)}: {formatPrice(d.revenue)} ({d.orders} sipariş)
              </title>
            </g>
          );
        })}

        {/* Line for Orders */}
        {numericData.length > 1 && (
          <g>
            <path
              d={numericData.map((d, index) => {
                const x = padding.left + (index * (barWidth + 2)) + (barWidth / 2);
                const ordersHeight = (d.orders / maxOrders) * chartHeight;
                const y = padding.top + chartHeight - ordersHeight;
                return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
              }).join(' ')}
              fill="none"
              stroke="#2A9D8F"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {numericData.map((d, index) => {
              const x = padding.left + (index * (barWidth + 2)) + (barWidth / 2);
              const ordersHeight = (d.orders / maxOrders) * chartHeight;
              const y = padding.top + chartHeight - ordersHeight;
              return (
                <circle
                  key={`order-point-${index}`}
                  cx={x}
                  cy={y}
                  r="5"
                  fill="#2A9D8F"
                  stroke="white"
                  strokeWidth="2"
                  className="hover:r-7 transition-all cursor-pointer"
                />
              );
            })}
          </g>
        )}

        {/* X-axis labels - with better spacing */}
        {numericData.map((d, index) => {
          const x = padding.left + (index * (barWidth + 2)) + (barWidth / 2);
          const showLabel = period === 'today' 
            ? index % Math.ceil(numericData.length / 12) === 0
            : index % Math.ceil(numericData.length / 10) === 0 || index === numericData.length - 1;
          
          if (!showLabel) return null;
          
          return (
            <text
              key={`label-${index}`}
              x={x}
              y={chartHeight + padding.top + 25}
              textAnchor="middle"
              fontSize="11"
              fill="#374151"
              fontWeight="500"
            >
              {formatDateLabel(d)}
            </text>
          );
        })}

        {/* X-axis title */}
        <text
          x={(padding.left + chartWidth - padding.right) / 2}
          y={chartHeight + padding.top + 50}
          textAnchor="middle"
          fontSize="13"
          fill="#6b7280"
          fontWeight="600"
        >
          Tarih
        </text>

        {/* Y-axis title for Revenue (left) */}
        <text
          x={padding.left / 2 - 10}
          y={padding.top + chartHeight / 2}
          textAnchor="middle"
          fontSize="13"
          fill="#E63946"
          fontWeight="700"
          transform={`rotate(-90 ${padding.left / 2 - 10} ${padding.top + chartHeight / 2})`}
        >
          Gelir (₺)
        </text>

        {/* Y-axis title for Orders (right) */}
        <text
          x={chartWidth - padding.right / 2}
          y={padding.top + chartHeight / 2}
          textAnchor="middle"
          fontSize="13"
          fill="#2A9D8F"
          fontWeight="700"
          transform={`rotate(90 ${chartWidth - padding.right / 2} ${padding.top + chartHeight / 2})`}
        >
          Sipariş Sayısı
        </text>
      </svg>
    </div>
  );
}

export default AnalyticsTab;
